"use client";
import React from "react";
import type {
  TextFieldProps as AriaTextFieldProps,
  TextFieldRenderProps,
  ValidationResult,
} from "react-aria-components";
import { TextField as AriaTextFieldComponent } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useDelayedLoading } from "../../hooks/useDelayedLoading";
import { composeTailwindRenderProps } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import {
  BaseInputProps,
  ClearButton,
  Description,
  FieldError,
  getInputBaseStyles,
  Input,
  InputWrapper,
  Label,
  useInputFocus,
} from "../Field";
import { Icon } from "../Icon/Icon";
import { Loader } from "../Loader";

const getTextFieldStyles = (props: {
  isInvalid?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  transparent?: boolean;
  size?: Size;
  showSearchIcon?: boolean;
  isClearable?: boolean;
  hasValue?: boolean;
  isPassword?: boolean;
  className?: string;
}): string => {
  // Get base input styles
  const baseStyles = getInputBaseStyles({
    isInvalid: props.isInvalid,
    isDisabled: props.isDisabled,
    isFocused: props.isFocused,
    transparent: props.transparent,
    size: props.size,
  });

  // Add TextField-specific padding styles
  const paddingStyles = [
    props.showSearchIcon ? "pl-8" : "pl-3",
    (props.isClearable && props.hasValue) || props.isPassword ? "pr-8" : "pr-3",
  ].join(" ");

  return twMerge(baseStyles, paddingStyles, props.className);
};

/**
 * TextField
 *
 * A controlled or uncontrolled text input with Edges styling, label,
 * description, error messaging, and optional affordances like a search icon,
 * clear button, and password visibility toggle. Wraps
 * `react-aria-components` TextField for accessible behavior.
 */
export interface TextFieldProps extends Omit<AriaTextFieldProps, "isRequired" | "size" | "className">, BaseInputProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  tooltip?: string;
  isRequired?: boolean;
  className?: string;
  validationResult?: ValidationResult;
  /**
   * Whether to reserve space for error messages to prevent layout shift
   * @default true
   */
  reserveErrorSpace?: boolean;
  /**
   * Shows a loading spinner instead of the search icon (only applicable when showSearchIcon is true)
   * @default false
   */
  isLoading?: boolean;
  /**
   * Where to display the description text
   * - "below": Show below the field (default)
   * - "tooltip": Show as a tooltip on the label icon
   * - "inline": Show inline with the label
   * - "hidden": Don't display the description
   * @default "below"
   */
  descriptionPlacement?: "below" | "tooltip" | "inline" | "hidden";
}

/**
 * Renders an Edges TextField with label, description, validation states and
 * optional search/clear/password affordances.
 */
export function TextField({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  isRequired,
  transparent,
  showSearchIcon,
  isClearable,
  onClear,
  type,
  validationResult,
  reserveErrorSpace = true,
  isLoading = false,
  descriptionPlacement = "below",
  ...props
}: TextFieldProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const { isFocused, handleFocus, handleBlur } = useInputFocus();
  const isPassword = type === "password";
  const isLoadingDelayed = useDelayedLoading(isLoading, 300);

  return (
    <AriaTextFieldComponent
      {...props}
      isRequired={isRequired}
      className={composeTailwindRenderProps(props.className, "group flex flex-col")}
    >
      {({ isDisabled, isInvalid }: TextFieldRenderProps) => (
        <div className="flex flex-col">
          {label && (
            <Label
              size={size}
              tooltip={tooltip}
              description={
                descriptionPlacement === "tooltip" || descriptionPlacement === "inline" ? description : undefined
              }
              descriptionPlacement={descriptionPlacement}
              isRequired={isRequired}
            >
              {label}
            </Label>
          )}
          <InputWrapper>
            {showSearchIcon && (
              <div className="pointer-events-none absolute left-2 top-[45%] -translate-y-1/2 transform flex items-center z-10">
                {isLoadingDelayed ? (
                  <Loader size={16} color="text-text-muted" />
                ) : (
                  <Icon
                    name="MagnifyingGlass"
                    className={`h-4 w-4 transition-colors ${isFocused ? "text-action-brand" : "text-text-muted"}`}
                  />
                )}
              </div>
            )}
            <Input
              size={size}
              disabled={isDisabled}
              type={isPassword ? (showPassword ? "text" : "password") : type}
              value={props.value as string | undefined}
              defaultValue={props.defaultValue as string | undefined}
              placeholder={props.placeholder}
              onFocus={(e) => handleFocus(e, props.onFocus)}
              onBlur={(e) => handleBlur(e, props.onBlur)}
              onChange={(e) => props.onChange?.(e.target.value)}
              className={getTextFieldStyles({
                isInvalid,
                isDisabled,
                isFocused,
                size,
                transparent,
                showSearchIcon,
                isClearable,
                hasValue: !!props.value,
                isPassword,
                className: typeof props.className === "string" ? props.className : undefined,
              })}
            />
            {isClearable && props.value && !isDisabled && (
              <ClearButton
                onClick={() => {
                  if (onClear) {
                    onClear();
                  } else if (props.onChange) {
                    props.onChange("");
                  }
                }}
                size={size}
              />
            )}
            {isPassword && !isDisabled && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transform text-text-muted hover:text-text-body"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Icon
                  name={showPassword ? "EyeSlash" : "Eye"}
                  size={size === "sm" ? 16 : 20}
                  className="text-text-body"
                />
              </button>
            )}
          </InputWrapper>
          {reserveErrorSpace ? (
            <div className="mt-1 min-h-[20px]">
              {typeof errorMessage === "string" ? (
                <FieldError size={size}>{errorMessage}</FieldError>
              ) : typeof errorMessage === "function" && validationResult ? (
                <FieldError size={size}>{errorMessage(validationResult)}</FieldError>
              ) : description && descriptionPlacement === "below" ? (
                <Description size={size}>{description}</Description>
              ) : null}
            </div>
          ) : (
            <>
              {typeof errorMessage === "string" ? (
                <FieldError size={size} className="mt-1">
                  {errorMessage}
                </FieldError>
              ) : typeof errorMessage === "function" && validationResult ? (
                <FieldError size={size} className="mt-1">
                  {errorMessage(validationResult)}
                </FieldError>
              ) : description && descriptionPlacement === "below" ? (
                <Description size={size} className="mt-1">
                  {description}
                </Description>
              ) : null}
            </>
          )}
        </div>
      )}
    </AriaTextFieldComponent>
  );
}
